import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { OnlineMeeting } from '@microsoft/microsoft-graph-types';
import { of } from 'rxjs/internal/observable/of';
import { Subject } from 'rxjs/internal/Subject';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { user } from 'src/app/models/user.model';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { NotificationService } from 'src/app/services/notification.service';
import { TeamsMeetingService } from 'src/app/services/teams-meeting.service';
import { copyToClipboard } from 'src/app/utils/copy';
import { MeetingLinkDocentDTO } from 'src/app/models/meeting-link-docent-dto.model';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TooltipModule } from 'primeng/tooltip';
import { DialogLink } from 'src/app/components/link-button/link-button';

@Component({
  selector: 'app-meeting-link-docent-actions',
  standalone: true,
  imports: [ButtonModule, DialogLink, FloatLabelModule, TooltipModule],
  templateUrl: './meeting-link-docent-actions.component.html',
  styleUrl: './meeting-link-docent-actions.component.scss',
})
export class MeetingLinkDocentActionsComponent implements OnDestroy {
  @Input({ required: true }) meetingData: MeetingLinkDocentDTO;
  @Input({ required: true }) lecturerUsers: user[] = [];
  @Output() meetingChanged: EventEmitter<void> = new EventEmitter<void>();

  /** Das Online-Meeting, das von Microsoft Graph abgerufen wurde */
  onlineMeeting?: OnlineMeeting;
  copyToClipboard = copyToClipboard;
  isLoadingTeamsMeeting: boolean = false;

  /** Destroys subscription on component destroy */
  private destroy$: Subject<boolean> = new Subject();

  constructor(
    readonly notificationService: NotificationService,
    private readonly teamsMeetingService: TeamsMeetingService,
    private readonly dialogManagerService: DialogManagerService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * Öffnet den Dialog zum Verwalten des Teams-Meetings.
   * Lädt zuerst die Meeting-Daten von Microsoft Graph.
   * Wenn bereits ein Teams-Meeting existiert, wird eine Bearbeitungsansicht geöffnet, andernfalls eine Ansicht zum Erstellen eines neuen Meetings.
   */
  openTeamsMeetingDialog(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.isLoadingTeamsMeeting = true;
    this.loadOnlineMeeting()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.openDialog();
          this.isLoadingTeamsMeeting = false;
        },
        error: (error) => {
          this.notificationService.error(
            'Fehler beim Laden des Online-Meetings. Bitte versuchen Sie es erneut.',
            error,
          );
          this.isLoadingTeamsMeeting = false;
        },
      });
  }

  private openDialog() {
    this.openTeamsMeetingDialogDocent();
  }

  private openTeamsMeetingDialogDocent() {
    this.dialogManagerService.openTeamsMeetingDialogDocent(
      this.meetingData.docentName,
      this.lecturerUsers,
      // Übergebe Callback, das Callback hat auch einen Parameter, den es dann zurückbekommt
      // Dieser Parameter enthält die Information, ob ein Meeting erstellt oder bearbeitet wurde, damit wir wissen, ob wir die Daten neu laden müssen
      ((result) => {
        if (result) {
          this.meetingChanged.emit();
        }
      }).bind(this),
      this.onlineMeeting,
    );
  }

  private loadOnlineMeeting() {
    if (!this.meetingData.meetingId) {
      this.onlineMeeting = null; // Setze onlineMeeting zurück, für den Fall, dass das Meeting zuvor geladen wurde, aber jetzt nicht mehr existiert
      return of(null); // Wenn kein Teams-Meeting vorhanden ist, gibt es auch kein Online-Meeting zu laden
    }
    return this.teamsMeetingService.get(this.meetingData.meetingId).pipe(
      tap((meeting) => {
        this.onlineMeeting = meeting;
      }),
      catchError((error) => {
        this.onlineMeeting = null;
        this.notificationService.error('Fehler beim Laden des Online-Meetings');
        throw error; // Fehler weiterwerfen, damit der Aufrufer weiß, dass das Laden fehlgeschlagen ist
      }),
    );
  }
}
